// Refactor 02/2026 — extrahiert aus AppointmentsPage.js. Verwaltet die
// Trainer-Spalten-Reihenfolge pro Studio inkl. Drag&Drop-Header.
//
// Behaviour ist unverändert:
//   - Order wird pro Studio lokal gecached.
//   - Auf "studioFilter"-Wechsel wird die Order vom Backend nachgeladen,
//     falls noch nicht im Cache.
//   - Drop-Aktion verschiebt den Trainer in der Liste und persistiert
//     fire-and-forget via updateTrainerOrderApi.
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  fetchTrainerOrderApi,
  updateTrainerOrderApi,
} from '@/services/appointmentsApi';

export const useTrainerColumnReorder = ({
  studioFilter,
  filteredTrainers,
}) => {
  const [trainerOrderByStudio, setTrainerOrderByStudio] = useState({});
  const [draggingTrainerId, setDraggingTrainerId] = useState(null);
  const [dragOverTrainerId, setDragOverTrainerId] = useState(null);

  // Order pro Studio laden (nur einmal pro Studio cachen)
  useEffect(() => {
    if (!studioFilter || studioFilter === 'all') return;
    if (trainerOrderByStudio[studioFilter] !== undefined) return;
    (async () => {
      try {
        const order = await fetchTrainerOrderApi(studioFilter);
        setTrainerOrderByStudio((prev) => ({
          ...prev,
          [studioFilter]: order,
        }));
      } catch (e) {
        console.error('Konnte Trainer-Reihenfolge nicht laden:', e);
      }
    })();
  }, [studioFilter, trainerOrderByStudio]);

  // Sortier-Helper: gespeicherte Order anwenden, unbekannte Trainer ans Ende.
  const orderedFilteredTrainers = useMemo(() => {
    const order = trainerOrderByStudio[studioFilter] || [];
    if (!order.length) return filteredTrainers;
    const indexOf = (id) => {
      const idx = order.indexOf(id);
      return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
    };
    return [...filteredTrainers].sort((a, b) => {
      const ai = indexOf(a.trainer.id);
      const bi = indexOf(b.trainer.id);
      if (ai !== bi) return ai - bi;
      const an = `${a.trainer.first_name || ''} ${
        a.trainer.last_name || ''
      }`.toLowerCase();
      const bn = `${b.trainer.first_name || ''} ${
        b.trainer.last_name || ''
      }`.toLowerCase();
      return an.localeCompare(bn);
    });
  }, [filteredTrainers, trainerOrderByStudio, studioFilter]);

  const handleTrainerColumnDrop = (targetTrainerId) => {
    if (!draggingTrainerId || draggingTrainerId === targetTrainerId) {
      setDraggingTrainerId(null);
      setDragOverTrainerId(null);
      return;
    }
    if (!studioFilter || studioFilter === 'all') {
      setDraggingTrainerId(null);
      setDragOverTrainerId(null);
      return;
    }
    const currentIds = orderedFilteredTrainers.map((t) => t.trainer.id);
    const fromIdx = currentIds.indexOf(draggingTrainerId);
    const toIdx = currentIds.indexOf(targetTrainerId);
    if (fromIdx === -1 || toIdx === -1) {
      setDraggingTrainerId(null);
      setDragOverTrainerId(null);
      return;
    }
    const newOrder = [...currentIds];
    newOrder.splice(fromIdx, 1);
    newOrder.splice(toIdx, 0, draggingTrainerId);

    setTrainerOrderByStudio((prev) => ({
      ...prev,
      [studioFilter]: newOrder,
    }));
    setDraggingTrainerId(null);
    setDragOverTrainerId(null);

    // Persistieren (fire-and-forget – User-Feedback via toast)
    updateTrainerOrderApi(studioFilter, newOrder).catch((e) => {
      console.error('Konnte Trainer-Reihenfolge nicht speichern:', e);
      toast.error('Reihenfolge konnte nicht gespeichert werden');
    });
  };

  return {
    orderedFilteredTrainers,
    draggingTrainerId,
    setDraggingTrainerId,
    dragOverTrainerId,
    setDragOverTrainerId,
    handleTrainerColumnDrop,
  };
};

export default useTrainerColumnReorder;
